export type ResponseData<T extends any = any> = T extends any
  ? {
      msg?: string;
      data: T;
      code?: number;
      error?: any;
    }
  : never;

export const headersJSON = { "Content-Type": "application/json" };

const URL_ONLINE = "https://www.loopupai.com/";
const TIMEOUT = 5000;

export const serverUrl = "/";
// export const serverUrl = "https://www.jiajiaclass.com/";
// export const serverUrl = "http://192.168.31.42:13005/";
// const prefixUrl = `http://localhost:3005/api/`;
const prefixUrl = `${serverUrl}api/`;

interface FetchOptions extends RequestInit {
  params?: Record<string, any>;
  data?: Record<string, any>;
  headers?: any;
  refresh?: () => void;
  prefixUrl?: string;
}

const originalFetch = window.fetch;

// 重写 fetch
window.fetch = async function (url, options = {}) {
  try {
    if (/(\.png|\.jpg|.gif)$/.test(url as string)) {
      url += `?v=${+new Date()}`;
    }
    // 调用原始 fetch
    const response = await originalFetch(url, options);
    // 返回解析后的数据
    return await response;
  } catch (error) {
    console.error("Fetch Error Caught:", error);
    throw error; // 继续抛出错误
  }
};

// 使用 fetch api 封装一个公用的请求方法
export const request = async (
  path: string,
  options?: FetchOptions,
  noTimeOut: boolean = false
) => {
  const reOptions = options ?? {};
  if (options?.data) {
    if (
      options?.headers &&
      options?.headers["Content-Type"] === "multipart/form-data"
    ) {
      const formData = new FormData();
      Object.keys(options.data).forEach((key) => {
        formData.append(key, options.data![key]);
      });
      reOptions.body = formData;
    } else {
      reOptions.body = JSON.stringify(options.data);
    }
  }

  let resp;
  if (noTimeOut) {
    resp = await originalFetch(
      `${
        path.indexOf("http") === 0 ? "" : options?.prefixUrl || prefixUrl
      }${path}${
        options?.params
          ? `?${new URLSearchParams(options.params).toString()}`
          : ""
      }`,
      {
        headers: {
          "accept-encoding": "br",
          ...options?.headers,
        },
        ...reOptions,
      }
    ).finally();
  } else {
    const controller = new AbortController();

    // 设定超时取消请求
    const timeoutId = setTimeout(() => controller.abort(), TIMEOUT);
    resp = await originalFetch(
      `${
        path.indexOf("http") === 0 ? "" : options?.prefixUrl || prefixUrl
      }${path}${
        options?.params
          ? `?${new URLSearchParams(options.params).toString()}`
          : ""
      }`,
      {
        headers: {
          "accept-encoding": "br",
          ...options?.headers,
        },
        ...reOptions,
        signal: controller.signal,
      }
    ).finally(() => clearTimeout(timeoutId));
  }

  const data = await resp.json();
  return data;
};

export const requestStream = async (
  path: string,
  options?: FetchOptions,
  onChunk?: (chunk: any) => void,
  noTimeOut: boolean = true // 流式请求默认不超时
) => {
  const reOptions = options ?? {};
  if (options?.data) {
    if (
      options?.headers &&
      options?.headers["Content-Type"] === "multipart/form-data"
    ) {
      const formData = new FormData();
      Object.keys(options.data).forEach((key) => {
        formData.append(key, options.data![key]);
      });
      reOptions.body = formData;
    } else {
      reOptions.body = JSON.stringify(options.data);
    }
  }

  let resp: Response;
  if (noTimeOut) {
    resp = await originalFetch(
      `${
        path.indexOf("http") === 0 ? "" : options?.prefixUrl || prefixUrl
      }${path}${
        options?.params
          ? `?${new URLSearchParams(options.params).toString()}`
          : ""
      }`,
      {
        headers: {
          "accept-encoding": "br",
          ...options?.headers,
        },
        ...reOptions,
      }
    );
  } else {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), TIMEOUT);
    resp = await originalFetch(
      `${
        path.indexOf("http") === 0 ? "" : options?.prefixUrl || prefixUrl
      }${path}${
        options?.params
          ? `?${new URLSearchParams(options.params).toString()}`
          : ""
      }`,
      {
        headers: {
          "accept-encoding": "br",
          ...options?.headers,
        },
        ...reOptions,
        signal: controller.signal,
      }
    ).finally(() => clearTimeout(timeoutId));
  }

  if (!resp.ok) {
    throw new Error(`HTTP error! status: ${resp.status}`);
  }

  // 读取流式响应
  const reader = resp.body?.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  const audioChunks: string[] = [];
  let sentence: any = null;
  let finalCode: number | null = null;
  let finalMessage: string = "";

  if (!reader) {
    throw new Error("Response body is not readable");
  }

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      // 解码数据并添加到缓冲区
      buffer += decoder.decode(value, { stream: true });

      // 按行分割并处理每行JSON
      const lines = buffer.split("\n");
      // 保留最后一个不完整的行在缓冲区中
      buffer = lines.pop() || "";

      for (const line of lines) {
        const trimmedLine = line.trim();
        if (!trimmedLine) continue;

        try {
          const jsonData = JSON.parse(trimmedLine);

          // 调用回调函数处理每个chunk
          if (onChunk) {
            onChunk(jsonData);
          }

          // 处理音频数据
          if (jsonData.data && typeof jsonData.data === "string") {
            audioChunks.push(jsonData.data);
          }

          // 处理文本信息
          if (jsonData.sentence) {
            sentence = jsonData.sentence;
          }

          // 处理完成状态
          if (jsonData.code === 20000000) {
            finalCode = jsonData.code;
            finalMessage = jsonData.message || "ok";
          }
        } catch (parseError) {
          // 忽略解析错误，继续处理下一行
          console.warn("Failed to parse JSON line:", trimmedLine, parseError);
        }
      }
    }

    // 处理缓冲区中剩余的数据
    if (buffer.trim()) {
      try {
        const jsonData = JSON.parse(buffer.trim());
        if (onChunk) {
          onChunk(jsonData);
        }
        if (jsonData.data && typeof jsonData.data === "string") {
          audioChunks.push(jsonData.data);
        }
        if (jsonData.sentence) {
          sentence = jsonData.sentence;
        }
        if (jsonData.code === 20000000) {
          finalCode = jsonData.code;
          finalMessage = jsonData.message || "ok";
        }
      } catch (parseError) {
        console.warn("Failed to parse final buffer:", buffer, parseError);
      }
    }

    // 拼接所有base64音频数据
    const combinedAudioData = audioChunks.join("");

    return {
      code: finalCode || 0,
      message: finalMessage,
      data: combinedAudioData || null,
      sentence: sentence,
      audioChunks: audioChunks, // 保留分块数据，方便调试
    };
  } catch (error) {
    console.error("Stream reading error:", error);
    throw error;
  }
};
